import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import FlagIcon from './FlagIcon';
import Avatar from './Avatar';
import { supabase } from '../supabaseClient';
import { invalidateListCache } from '../utils/listCache';
import { getFriendDisplayName } from '../utils/displayName';

export default function BlockedFriendsSheet({
  isOpen,
  onClose,
  onShowToast,
  onUnblock
}) {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isAnimate, setIsAnimate] = useState(false);
  const [blockedList, setBlockedList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unblockingIds, setUnblockingIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      fetchBlockedFriends();
      const timer = setTimeout(() => {
        setIsAnimate(true);
      }, 15);
      return () => clearTimeout(timer);
    } else {
      setIsAnimate(false);
      const timer = setTimeout(() => {
        setIsMounted(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const fetchBlockedFriends = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('friendships')
        .select('friend_id, nickname, profiles!friend_id(id, name, nickname, name_display_choice, my_language, flag_code, avatar, avatar_selected)')
        .eq('user_id', user.id)
        .eq('status', 'blocked');

      if (error) {
        console.error('Error fetching blocked friends:', error);
        return;
      }

      const formatted = (data || []).map((item) => {
        const p = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles;
        return {
          friendId: item.friend_id,
          id: p?.id || item.friend_id,
          name: p?.name || 'Unknown',
          nickname: p?.nickname || null,
          name_display_choice: p?.name_display_choice || 'name',
          friendNickname: item.nickname || null,
          flagCode: p?.flag_code || 'UN',
          avatar: p?.avatar || null,
          avatarSelected: p?.avatar_selected ?? false,
          myLanguage: p?.my_language || 'en'
        };
      });
      setBlockedList(formatted);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnblock = async (friend) => {
    if (unblockingIds.includes(friend.friendId)) return;
    try {
      setUnblockingIds((prev) => [...prev, friend.friendId]);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.rpc('unblock_friend', {
        p_friend_id: friend.friendId,
      });

      if (error) {
        console.error('Error unblocking friend:', error);
        setUnblockingIds((prev) => prev.filter((id) => id !== friend.friendId));
        return;
      }

      invalidateListCache('friend_list');

      setTimeout(() => {
        setBlockedList((prev) => prev.filter((item) => item.friendId !== friend.friendId));
        setUnblockingIds((prev) => prev.filter((id) => id !== friend.friendId));
        if (onShowToast) {
          onShowToast(t('friends_sheet.unblock_toast', { name: getFriendDisplayName(friend.friendNickname, friend, friend.name) }));
        }
        onUnblock?.(friend.friendId);
      }, 300);
    } catch (err) {
      console.error(err);
      setUnblockingIds((prev) => prev.filter((id) => id !== friend.friendId));
    }
  };

  if (!isMounted) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] flex justify-center pointer-events-none">
      <div
        className={`w-full max-w-[500px] bg-[#fbf9f1] rounded-t-3xl border-t border-[#006565]/40 overflow-hidden flex flex-col relative transition-all duration-250 ease-out shadow-[0_-8px_24px_rgba(0,0,0,0.15)] max-h-[50vh] pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pointer-events-auto ${
          isAnimate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 상단 가운데 회색 손잡이 pill (핸들바) */}
        <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mt-2 mb-1 flex-shrink-0" />

        {/* 우측 상단 X(닫기) 버튼 */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 w-8 h-8 rounded-full flex items-center justify-center text-[#006565]/70 hover:bg-[#006565]/10 active:scale-90 transition-all z-10"
        >
          <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 0" }}>
            close
          </span>
        </button>

        {/* 헤더 */}
        <div className="px-5 pt-1 pb-3 pr-10 border-b border-surface-container-high/60 flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[22px] text-[#006565]">block</span>
            <h2 className="font-headline-sm text-[18px] font-bold text-[#006565]">{t('friends_sheet.blocked_title')}</h2>
          </div>
          <p className="font-body-sm text-[#888888] text-[12px] pl-0.5">
            {t('friends_sheet.blocked_desc')}
          </p>
        </div>

        {/* 목록 콘텐츠 영역 */}
        <div className="px-4 py-3 overflow-y-auto flex-grow flex flex-col gap-2">
          {isLoading ? (
            <div className="py-10 flex items-center justify-center text-on-surface-variant/60 text-sm">
              {t('friends_sheet.loading')}
            </div>
          ) : blockedList.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-[48px] text-on-surface-variant/40 mb-2">
                block
              </span>
              <p className="font-body-md text-on-surface-variant/70 text-[14px]">
                {t('friends_sheet.blocked_empty')}
              </p>
            </div>
          ) : (
            blockedList.map((friend) => {
              const isFading = unblockingIds.includes(friend.friendId);
              return (
                <div
                  key={friend.friendId}
                  className={`bg-surface-container-lowest rounded-2xl p-3 flex items-center justify-between custom-shadow border border-surface-container-high transition-all duration-300 ${
                    isFading ? 'opacity-0 -translate-x-4 scale-95' : 'opacity-100 translate-x-0 scale-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 flex items-center justify-center shrink-0 aspect-square">
                        <Avatar
                          userId={friend.id}
                          name={getFriendDisplayName(friend.friendNickname, friend, friend.name)}
                          avatarUrl={friend.avatar}
                          avatarSelected={friend.avatarSelected}
                          size="w-9 h-9"
                          className="shadow-sm border border-outline-variant/20"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-surface-container-lowest rounded-full p-[2px] shadow-sm flex items-center justify-center">
                        <FlagIcon code={friend.flagCode} className="w-3.5 h-2.5 rounded-full object-cover" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-headline-sm text-[15px] font-semibold text-on-surface truncate">
                        {getFriendDisplayName(friend.friendNickname, friend, friend.name)}
                      </h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUnblock(friend)}
                    className="px-3.5 py-1 rounded-full border border-[#006565] text-[#006565] font-semibold text-[13px] hover:bg-[#006565]/10 active:scale-95 transition-all flex-shrink-0 ml-2"
                  >
                    {t('friends_sheet.unblock_btn')}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
